import {ModalContext} from "app/app";
import {Fragment, useContext, useEffect, useState} from "react";
import axios from "axios";
import styles from "./styles.module.scss";
import {useAPI} from "../hooks/api";
import {toast} from "react-toastify";
import bindQuote from 'views/website/images/popups/bindQuote.png';

export function BindPopup() {
  const {modal, setModal} = useContext(ModalContext);
  const data = modal.data || {};
  const user = useAPI("/api/user");
  const [step, setStep] = useState(1);

  const [formData, setFormData] = useState({
    question1: "",
    question2: "",
    question3: "",
    question4: "",
  });

  // File upload state - specific document categories
  const getInitialUploads = () => {
    const baseUploads = [
      { id: 1, title: "Loss Runs (Last 3 Years)", required: true, files: [] },
      { id: 2, title: "Safety Manual", required: true, files: [] },
      { id: 3, title: "MVR for Each Driver", required: true, files: [] }
    ];

    // Add IFTA document if 15+ vehicles
    const vehicleCount = getVehicleCount();
    if (vehicleCount >= 15) {
      baseUploads.push({ id: 4, title: "IFTA Document", required: true, files: [] });
    }

    return baseUploads;
  };

  // Get vehicle count from various possible locations in the data
  const getVehicleCount = () => {
    return data?.number_of_vehicles || 
           data?.quote?.limits?.nbrOfTrucks || 
           data?.quote?.vehicles?.length || 
           0;
  };

  // Check if file uploads should be shown (6+ vehicles)
  const shouldShowFileUploads = () => {
    return getVehicleCount() >= 6;
  };

  const [fileUploads, setFileUploads] = useState(getInitialUploads());

  const [error, setError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const handleChange = (e) => {
    setFormData({...formData, [e.target.name]: e.target.value});
  };

  // Handle file uploads for each upload area
  const handleFileUpload = (uploadId, files) => {
    setFileUploads(prev => prev.map(upload => 
      upload.id === uploadId 
        ? { ...upload, files: Array.from(files) }
        : upload
    ));
  };

  // Update file uploads when data changes (for IFTA document)
  useEffect(() => {
    if (shouldShowFileUploads()) {
      setFileUploads(getInitialUploads());
    }
  }, [data?.number_of_vehicles, data?.quote?.limits?.nbrOfTrucks, data?.quote?.vehicles?.length]);

  useEffect(() => {
    if (data?.carrier === "Progressive") {
      setStep(2);
    }
  }, [data?.carrier]);

  const handleSubmit = async () => {
    if (Object.values(formData).some((value) => value.trim() === "")) {
      setError(true);
      return;
    }

    setError(false);
    setIsSubmitting(true);

    try {
      const account = await axios.get(`/api/account`);
      const formattedEffectiveDate = data?.effective_date
        ? new Date(data.effective_date).toISOString().split("T")[0]
        : null;

      // First upload all documents using /api/document endpoint (only if 6+ vehicles)
      const uploadedDocuments = [];
      let totalFiles = 0;
      if (shouldShowFileUploads()) {
        // Count total files for logging
        fileUploads.forEach(upload => {
          totalFiles += upload.files.length;
        });
        
        for (const upload of fileUploads) {
          if (upload.files.length > 0) {
            for (const file of upload.files) {
                          try {
              // Create document record
              const documentPayload = {
                name: file.name,
                type: upload.title.toLowerCase().replace(/[^a-z0-9]/g, '_'),
                uploaded_by: user?.first_name || 'Unknown', // Add uploaded_by field
              };
              
              // Add company_id if available, otherwise use quote_id as fallback
              if (data.company_id) {
                documentPayload.company_id = data.company_id;
              } else {
                documentPayload.company_id = data.id; // Use quote ID as fallback
              }
              
                            const documentResponse = await axios.post("/api/document", documentPayload);

              // Check for document ID in different response formats
              let documentId = null;
              if (documentResponse.data.data?.id) {
                documentId = documentResponse.data.data.id;
              } else if (documentResponse.data.id) {
                documentId = documentResponse.data.id;
              }

              if (documentResponse.data.success && documentId) {
                // Upload file to the created document
                const fileFormData = new FormData();
                fileFormData.append('file', file);
                
                const fileUploadResponse = await axios.post(`/api/document/${documentId}/file`, fileFormData, {
                  headers: {
                    'Content-Type': 'multipart/form-data',
                  },
                });

                if (fileUploadResponse.data.success) {
                  uploadedDocuments.push({
                    id: documentId,
                    title: upload.title,
                    fileName: file.name
                  });
                } else {
                  throw new Error(`Failed to upload file: ${fileUploadResponse.data.message || 'Unknown error'}`);
                }
              } else {
                throw new Error(`Failed to create document record: ${documentResponse.data.message || 'Unknown error'}`);
              }
                          } catch (uploadError) {
              console.error("Error uploading document:", uploadError);
              
              // More specific error messages
              if (uploadError.message.includes('Failed to upload file')) {
                toast.error(`Failed to upload file ${file.name}: ${uploadError.message}`);
              } else if (uploadError.response?.status === 400) {
                toast.error(`Invalid file ${file.name}. Please check file format and size.`);
              } else if (uploadError.response?.status === 413) {
                toast.error(`File ${file.name} is too large. Maximum size is 10MB.`);
              } else {
                toast.error(`Failed to upload ${file.name}. ${uploadError.response?.data?.message || uploadError.message || 'Please try again.'}`);
              }
              return;
            }
            }
          }
        }
      }

      // Then create bind request without files
      const res = await axios.post("/api/bind-requests", {
        quoteId: data.id,
        quote: data.carrier,
        userId: user.data.id,
        agencyId: account.data.data.id,
        agencyName: account.data.data.name,
        companyName: data?.company_name || data?.quote?.insuredInformation?.legalName,
        effectiveDate: formattedEffectiveDate,
        submissionNumber: data?.submission_number,
        assignedAgentEmail: account.data.data.owner_email,
        answerQuestion1: formData?.question1,
        answerQuestion2: formData?.question2,
        answerQuestion3: formData?.question3,
        answerQuestion4: formData?.question4,
        totalCost: data?.financialDetails.totalCost,
        uploadedDocuments: uploadedDocuments // Reference to uploaded documents
      });

      if (res.status === 200 || res.status === 201) {
        setSuccessMessage(
          "This submission has been sent to our processing team for binding." +
          " You can check the status of your request in the 'Bind Request' tab on the left side of your portal."
        );
        if (modal.onSuccess) {
          modal.onSuccess();
        }
        setStep(3);
      }
    } catch (error) {
      console.error("Error creating bind request:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong. Please try again later.";

      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setSuccessMessage("");
    setModal("");
    if (modal.action) {
      modal.action();
    }
    setStep(1);
    // Reset file uploads to initial state (only if they should be shown)
    if (shouldShowFileUploads()) {
      setFileUploads(getInitialUploads());
    }
  };

  return (
    <Fragment>
      {modal.name === "bind-popup" && (
        <div className={`${styles.background} ${step === 1 ? styles.step1 : ""}`}>
          <div className={styles.wrapper}>
            <div className={styles.modalContent}>

              {step === 1 && (
                <>
                  <div className={styles.imageContainer}>
                    <img src={bindQuote} alt="Bind Quote" className={styles.bindQuoteImage}/>
                  </div>
                  <div className={styles.footer}>
                    <div className={styles.buttons}>
                      <button className={styles.cancel} onClick={handleCloseModal}>
                        Cancel
                      </button>
                      <button className={styles.button0} onClick={() => setStep(2)}>
                        Confirm
                      </button>
                    </div>
                  </div>
                </>
              )}

              {step === 2 && !successMessage && (
                <>
                  <div className={styles.title}>Request to Bind</div>
                  
                  {/* Questions Section */}
                  <div className={styles.inputs}>
                    <label>Does the insured do any home deliveries including but not limited to moving
                      operations? <span className={styles.required}>*</span></label>
                    <input type="text" name="question1" value={formData.question1} onChange={handleChange}/>

                    <label>Does the insured visit construction sites at any time as part of their operation? <span className={styles.required}>*</span></label>
                    <input type="text" name="question2" value={formData.question2} onChange={handleChange}/>

                    <label>Please provide in detail, what the insured hauls (General freight is not considered
                      detailed) <span className={styles.required}>*</span></label>
                    <input type="text" name="question3" value={formData.question3} onChange={handleChange}/>

                    <label>Is the insured connected to any other entity or share drivers, equipment or vehicles with any
                      other entity? <span className={styles.required}>*</span></label>
                    <input type="text" name="question4" value={formData.question4} onChange={handleChange}/>
                  </div>

                  {/* File Upload Section - Only show for 6+ vehicles */}
                  {shouldShowFileUploads() ? (
                    <div className={styles.fileUploadSection}>
                      <div className={styles.sectionHeader}>
                        <h3>Required Documents</h3>
                        <p>Please upload the following documents to proceed with your bind request:</p>
                        {(() => {
                          const vehicleCount = getVehicleCount();
                          if (vehicleCount >= 15) {
                            return (
                              <div className={styles.notice}>
                                <strong>Note:</strong> Since you have {vehicleCount} vehicles (15+), an IFTA Document is also required.
                              </div>
                            );
                          } else if (vehicleCount >= 6) {
                            return (
                              <div className={styles.notice}>
                                <strong>Note:</strong> Since you have {vehicleCount} vehicles (6+), these documents are required for binding.
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                      {fileUploads.map((upload) => (
                        <div key={upload.id} className={styles.uploadArea}>
                          <div className={styles.uploadHeader}>
                            <span className={styles.uploadTitle}>
                              {upload.title}
                              {upload.required && <span className={styles.required}>*</span>}
                            </span>
                          </div>
                          <div className={styles.uploadBox}>
                            <input
                              type="file"
                              id={`file-${upload.id}`}
                              multiple
                              onChange={(e) => handleFileUpload(upload.id, e.target.files)}
                              style={{ display: 'none' }}
                            />
                            <label htmlFor={`file-${upload.id}`} className={styles.uploadLabel}>
                              <div className={styles.uploadIcon}>📁</div>
                              <div className={styles.uploadText}>
                                {upload.files.length > 0 ? (
                                  <span>
                                    {upload.files.length} file(s) selected
                                    <br />
                                    <small>{upload.files.map(f => f.name).join(', ')}</small>
                                  </span>
                                ) : (
                                  <span>
                                    Drag & drop files or <span className={styles.browseLink}>browse files</span>
                                    <br />
                                    <small>PDF, DOC, DOCX, JPG, PNG • Max file size 10MB per file</small>
                                  </span>
                                )}
                              </div>
                            </label>
                          </div>
                        </div>
                                              ))}
                      </div>
                  ) : (
                    <div className={styles.infoSection}>
                      <div className={styles.infoHeader}>
                        <h3>No Additional Documents Required</h3>
                        <p>Since you have {getVehicleCount()} vehicle(s), no additional documents are required for this bind request.</p>
                        <div className={styles.notice}>
                          <strong>Note:</strong> Additional documents are only required for quotes with 6 or more vehicles.
                        </div>
                      </div>
                    </div>
                  )}

                  {error && <div className={styles.error}>All question fields are required.</div>}

                  <div className={styles.footer}>
                    <button className={styles.cancel} onClick={handleCloseModal}>
                      Cancel
                    </button>
                    <button
                      className={styles.confirm}
                      onClick={handleSubmit}
                      disabled={isSubmitting || Object.values(formData).some((value) => value.trim() === "")}
                    >
                      {isSubmitting ? "Submitting..." : "Request"}
                    </button>
                  </div>
                </>
              )}

              {step === 3 && successMessage && (
                <div className={styles.successMessage}>
                  <div className={styles.title}>Success!</div>
                  <p>{successMessage}</p>
                  <button className={styles.confirm} onClick={handleCloseModal}>
                    OK
                  </button>
                </div>
              )}

            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
}
